import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign,
  Eye,
  Calendar,
  User,
  MapPin,
  Phone,
  Mail,
  FileText,
  Package,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  Box,
  Layout,
  Image as ImageIcon,
  FileDown,
  ChevronDown,
  ChevronRight,
  Plus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import surveyService from '../services/surveyService';
import projectsService from '../services/projectsService';
import SurveyView from './SurveyView';
import ConfirmModal from './ConfirmModal';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const BudgetsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showView, setShowView] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [expandedSurveys, setExpandedSurveys] = useState(new Set());
  const [creatingBudget, setCreatingBudget] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState({ show: false, survey: null });

  // Buscar levantamentos em Pré Projeto
  const { data: surveysData, isLoading, refetch } = useQuery({
    queryKey: ['surveys', 'PRE_PROJETO', searchTerm, filterPriority],
    queryFn: async () => {
      const response = await surveyService.getAllSurveys({
        status: 'PRE_PROJETO',
        client_name: searchTerm || undefined,
        priority: filterPriority || undefined,
        limit: 100
      });
      return response;
    },
    staleTime: 30000, // 30 segundos
  });

  const surveys = surveysData?.data || surveysData?.surveys || [];

  const handleView = async (survey) => {
    try {
      const response = await surveyService.getSurveyById(survey.id);
      setSelectedSurvey(response.data);
      setShowView(true);
    } catch (error) {
      console.error('Erro ao carregar levantamento:', error);
      toast.error('Erro ao carregar levantamento: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCreateBudget = async (survey) => {
    setConfirmModal({
      show: true,
      survey,
      onConfirm: () => executeCreateBudget(survey),
    });
  };

  const executeCreateBudget = async (survey) => {
    setConfirmModal({ show: false, survey: null });

    try {
      setCreatingBudget(new Set([...creatingBudget, survey.id]));
      
      // Criar projeto a partir do levantamento (ou buscar se já existir)
      const response = await projectsService.createProjectFromSurvey(survey.id);
      
      if (response.success && response.data) {
        const projectId = response.data.id;
        toast.success('Pré Projeto criado com sucesso! Redirecionando para o orçamento...');
        
        // Invalidar query para atualizar a lista com o projeto vinculado
        queryClient.invalidateQueries(['surveys', 'PRE_PROJETO']);
        
        // Navegar para a página de orçamento do projeto
        setTimeout(() => {
          navigate(`/projetos/${projectId}/orcamento`);
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao criar orçamento:', error);
      toast.error('Erro ao criar orçamento: ' + (error.response?.data?.message || error.message));
    } finally {
      const newSet = new Set(creatingBudget);
      newSet.delete(survey.id);
      setCreatingBudget(newSet);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Não informado';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getMaterialPreferencesText = (preferences) => {
    if (!preferences || typeof preferences !== 'object') return 'Não informado';
    
    const parts = [];
    
    if (preferences.material_type && Array.isArray(preferences.material_type) && preferences.material_type.length > 0) {
      parts.push(`Material: ${preferences.material_type.join(', ')}`);
    }
    
    if (preferences.hardware_type && Array.isArray(preferences.hardware_type) && preferences.hardware_type.length > 0) {
      parts.push(`Ferragens: ${preferences.hardware_type.join(', ')}`);
    }
    
    if (preferences.has_glass !== undefined) {
      parts.push(`Vidro: ${preferences.has_glass ? 'Sim' : 'Não'}`);
    }
    
    if (preferences.door_finish && Array.isArray(preferences.door_finish) && preferences.door_finish.length > 0) {
      parts.push(`Acabamento Portas: ${preferences.door_finish.join(', ')}`);
    }
    
    return parts.length > 0 ? parts.join(' | ') : 'Não informado';
  };

  const toggleSurvey = (surveyId) => {
    const newExpanded = new Set(expandedSurveys);
    if (newExpanded.has(surveyId)) {
      newExpanded.delete(surveyId);
    } else {
      newExpanded.add(surveyId);
    }
    setExpandedSurveys(newExpanded);
  };

  const isExpanded = (surveyId) => expandedSurveys.has(surveyId);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Orçamentos</h1>
              <p className="text-sm text-gray-600">
                Levantamentos em Pré Projeto aguardando orçamento
              </p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por cliente, código ou endereço..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Todas as prioridades</option>
              <option value="LOW">Baixa</option>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">Alta</option>
              <option value="URGENT">Urgente</option>
            </select>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{surveys.length}</p>
            </div>
            <DollarSign className="w-8 h-8 text-emerald-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Urgente</p>
              <p className="text-2xl font-bold text-red-600">
                {surveys.filter(s => s.priority === 'URGENT').length}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Alta</p>
              <p className="text-2xl font-bold text-orange-600">
                {surveys.filter(s => s.priority === 'HIGH').length}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Normal/Baixa</p>
              <p className="text-2xl font-bold text-blue-600">
                {surveys.filter(s => ['NORMAL', 'LOW'].includes(s.priority)).length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Botão para criar orçamento manual */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/orcamentos/criar')}
          className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-600 shadow-lg shadow-emerald-500/30 flex items-center gap-3 font-bold transition-all transform hover:scale-[1.02]"
        >
          <Plus className="w-5 h-5" />
          Criar Orçamento Manual
        </button>
      </div>

      {/* Lista de Levantamentos */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      ) : surveys.length === 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
          <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nenhum levantamento encontrado
          </h3>
          <p className="text-gray-600">
            {searchTerm || filterPriority
              ? 'Tente ajustar os filtros de busca.'
              : 'Não há levantamentos em Pré Projeto no momento.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {surveys.map((survey) => {
            const expanded = isExpanded(survey.id);
            return (
              <div
                key={survey.id}
                className="bg-white rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
              >
                {/* Header Clicável */}
                <button
                  onClick={() => toggleSurvey(survey.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg"
                >
                  <div className="flex items-center gap-4 flex-1 text-left">
                    <div className="flex-shrink-0">
                      {expanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-base font-semibold text-gray-900">
                          {surveyService.formatSurveyCode(survey.survey_code)}
                        </h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${surveyService.getPriorityColor(survey.priority)}`}>
                          {surveyService.formatPriority(survey.priority)}
                        </span>
                        {survey.project_id && survey.project_code && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3" />
                            Projeto: {survey.project_code}
                          </span>
                        )}
                        {survey.estimated_budget_range && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {survey.estimated_budget_range}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {survey.client_name}
                        </span>
                        {survey.client_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {survey.client_phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate max-w-xs">{survey.client_address}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(survey);
                      }}
                      className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 text-sm"
                      title="Ver detalhes completos"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">Detalhes</span>
                    </button>
                    {survey.project_id && survey.project_code ? (
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <span className="hidden sm:inline">Projeto:</span>
                          <span className="font-semibold">{survey.project_code}</span>
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projetos/${survey.project_id}/orcamento`);
                          }}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
                          title="Ver orçamento do projeto"
                        >
                          <DollarSign className="w-4 h-4" />
                          <span className="hidden sm:inline">Ver Orçamento</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateBudget(survey);
                        }}
                        disabled={creatingBudget.has(survey.id)}
                        className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
                        title="Criar orçamento personalizado"
                      >
                        {creatingBudget.has(survey.id) ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span className="hidden sm:inline">Criando...</span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-4 h-4" />
                            <span className="hidden sm:inline">Criar Orçamento</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </button>

                {/* Conteúdo Expandido */}
                {expanded && (
                  <div className="px-4 pb-4 border-t border-gray-200">
                    <div className="pt-4 space-y-4">

                      {/* Informações do Cliente */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="flex items-start gap-2">
                          <User className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Cliente</p>
                            <p className="text-sm font-medium text-gray-900">{survey.client_name}</p>
                          </div>
                        </div>
                        {survey.client_phone && (
                          <div className="flex items-start gap-2">
                            <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-gray-500">Telefone</p>
                              <p className="text-sm font-medium text-gray-900">{survey.client_phone}</p>
                            </div>
                          </div>
                        )}
                        {survey.client_email && (
                          <div className="flex items-start gap-2">
                            <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="text-sm font-medium text-gray-900">{survey.client_email}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Endereço</p>
                            <p className="text-sm font-medium text-gray-900">
                              {survey.client_address}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Informações Técnicas */}
                      <div className="border-t border-gray-200 pt-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Informações Técnicas</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {survey.environment_description && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Descrição do Ambiente</p>
                              <p className="text-sm text-gray-900">
                                {survey.environment_description}
                              </p>
                            </div>
                          )}
                          {survey.technical_observations && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Observações Técnicas</p>
                              <p className="text-sm text-gray-900">
                                {survey.technical_observations}
                              </p>
                            </div>
                          )}
                          {survey.plumbing_info && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Informações de Encanamento</p>
                              <p className="text-sm text-gray-900">
                                {survey.plumbing_info}
                              </p>
                            </div>
                          )}
                          {survey.furniture_purpose && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Utilidade dos Móveis</p>
                              <p className="text-sm text-gray-900">
                                {survey.furniture_purpose}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Preferências de Material */}
                      {survey.material_preferences && (
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            Preferências de Material
                          </h4>
                          <p className="text-sm text-gray-700">
                            {getMaterialPreferencesText(survey.material_preferences)}
                          </p>
                        </div>
                      )}

                      {/* Datas Importantes */}
                      <div className="border-t border-gray-200 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {survey.survey_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Data do Levantamento</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {formatDate(survey.survey_date)}
                                </p>
                              </div>
                            </div>
                          )}
                          {survey.project_deadline && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Prazo Desejado</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {formatDate(survey.project_deadline)}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Informações Adicionais */}
                      <div className="border-t border-gray-200 pt-4 flex flex-wrap gap-4">
                        {survey.files && survey.files.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <ImageIcon className="w-4 h-4" />
                            <span>{survey.files.length} arquivo(s)</span>
                          </div>
                        )}
                        {survey.files_3d && survey.files_3d.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Box className="w-4 h-4" />
                            <span>{survey.files_3d.length} arquivo(s) 3D</span>
                          </div>
                        )}
                        {survey.sketch_project_id && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Layout className="w-4 h-4" />
                            <span>Croqui 2D disponível</span>
                          </div>
                        )}
                        {survey.project_3d_status === 'COMPLETED' && (
                          <div className="flex items-center gap-2 text-sm text-emerald-600">
                            <CheckCircle className="w-4 h-4" />
                            <span>Projeto 3D concluído</span>
                          </div>
                        )}
                      </div>

                      {/* Perfil do Cliente (apenas GERENCIA) */}
                      {user.role === 'GERENCIA' && survey.client_profile && Object.keys(survey.client_profile).length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">Perfil do Cliente</h4>
                          <div className="text-sm text-gray-700">
                            {survey.client_profile.demanding && (
                              <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded mr-2 mb-2">
                                Exigente
                              </span>
                            )}
                            {survey.client_profile.existing_quality && (
                              <p className="mb-1">
                                <span className="font-medium">Qualidade existente:</span>{' '}
                                {survey.client_profile.existing_quality}
                              </p>
                            )}
                            {survey.client_profile.behavior_notes && (
                              <p>
                                <span className="font-medium">Observações comportamentais:</span>{' '}
                                {survey.client_profile.behavior_notes}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="text-xs text-gray-500 pt-2">
                        Criado em {formatDateTime(survey.created_at)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Visualização Detalhada */}
      {showView && selectedSurvey && (
        <SurveyView
          survey={selectedSurvey}
          onClose={() => {
            setShowView(false);
            setSelectedSurvey(null);
          }}
          onEdit={() => {
            setShowView(false);
            setSelectedSurvey(null);
            // Futuramente: abrir formulário de orçamento
          }}
        />
      )}

      <ConfirmModal
        show={confirmModal.show}
        title="Criar Orçamento"
        message={`Deseja criar um orçamento para o levantamento ${confirmModal.survey ? surveyService.formatSurveyCode(confirmModal.survey.survey_code) : ''}?\n\nUm projeto será criado automaticamente se ainda não existir.`}
        confirmText="Criar"
        cancelText="Cancelar"
        type="success"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, survey: null })}
      />
    </div>
  );
};

export default BudgetsList;
